import { defineStore } from "pinia"

import * as customersService from "@/services/customersService.js"
import * as licencesService from "@/services/licencesService.js"
import * as webUsersService from "@/services/webUsersService.js"
import * as crmAccountsService from "@/services/crmAccountsService.js"
import * as dictionariesService from "@/services/dictionariesService.js"
import * as datesService from "@/services/datesService.js"

import { useSessionStore } from "@/stores/userSession.js"

export const useAppDataStore = defineStore("appData", {
  state: () => ({
    allCustomers: [],
    allUsers: [],
    allCrmAccounts: [],
    allLicences: [],
    dictionaries: [],
    loadingInitData: false,
    loadingCustomers: false,
    loadingLicences: false,
    savingCustomer: false
  }),
  getters: {
    customerForId: (state) => (id) => state.allCustomers.find((c) => c.id === id),
    crmAccountForUserId: (state) => (id) => {
      const user = state.allUsers.find((u) => u.id === id)
      if (user == null) return null
      return state.allCrmAccounts.find((a) => a.id === user.crmAccountId)
    }
  },
  actions: {
    // Only the licences need anything else: their enrichment reads dictionaries.modules and
    // customerForId. So every request starts at once and just the enrichment waits, which beats
    // both the old sequential chain and a dependency-ordered pair of Promise.all waves.
    async fetchInitData() {
      if (this.loadingInitData) return
      try {
        this.loadingInitData = true
        this.loadingLicences = true

        const licencesPromise = licencesService.getAllSummary()
        // Mark the rejection handled so a failure elsewhere cannot turn this into an
        // unhandled rejection; awaiting it below still surfaces the error.
        licencesPromise.catch(() => {})

        const reference = [this.fetchDictionaries(), this.fetchAllCustomers(), this.fetchAllUsers()]

        // Only the admin panel lists the CRM accounts; everywhere else they are read to name
        // whoever created a record. The endpoint wants an admin role, so asking for them
        // without one earns a 403 the account could have known to avoid.
        const sessionStore = useSessionStore()
        if (sessionStore.hasRole("admin_view") || sessionStore.hasRole("admin_edit")) {
          reference.push(this.fetchAllCrmAccounts())
        }

        // Settled rather than all: these four are independent of each other and of the
        // licences. Promise.all rejected on the first failure, which skipped applyLicences
        // below entirely - an account that could not read one of them was handed an empty
        // licence list rather than a list missing a column.
        await Promise.allSettled(reference)

        this.applyLicences((await licencesPromise).data)
      } finally {
        this.loadingLicences = false
        this.loadingInitData = false
      }
    },
    async fetchDictionaries() {
      this.dictionaries = await dictionariesService.getDictionaries()
    },
    async fetchAllUsers() {
      const response = await webUsersService.getAll()
      this.allUsers = response.data
    },
    async fetchAllCrmAccounts() {
      const response = await crmAccountsService.getAll()
      this.allCrmAccounts = response.data
    },
    async fetchAllCustomers() {
      try {
        this.loadingCustomers = true
        const response = await customersService.getAll()
        const customers = response.data
        customers.forEach((customer, index) => {
          customer.index = index + 1
          if (customer.address) {
            customer.addressDisplay =
              `${customer.address.street || ""} ${customer.address.buildingNo || ""} ` +
              `${customer.address.postalCode || ""} ${customer.address.city || ""}`
            customer.country = customer.address.country
            customer.addressDisplayExtended = `${customer.addressDisplay} ${customer.address.country || ""}`
          } else {
            customer.addressDisplay = ""
            customer.addressDisplayExtended = ""
            customer.country = ""
            customer.address = {}
          }
          customer.fullName = `${customer.name} ${customer.surname || ""}`
        })
        this.allCustomers = customers
      } finally {
        this.loadingCustomers = false
      }
    },
    // quiet skips the loading flag, which drives the licences list progress bar - a background
    // refresh must not make that table flash every time it ticks.
    async fetchAllLicences(quiet = false) {
      try {
        if (!quiet) this.loadingLicences = true
        const response = await licencesService.getAllSummary()
        this.applyLicences(response.data)
      } finally {
        if (!quiet) this.loadingLicences = false
      }
    },
    // Derived fields the tables and CSV exports read. Needs dictionaries and customers loaded.
    applyLicences(licences) {
      // Empty rather than undefined: fetchInitData loads the dictionaries first, but
      // fetchAllLicences is also called on its own and would otherwise throw here, leaving
      // the list stale with nothing on screen to say why.
      const modules = this.dictionaries.modules ?? []
      licences.forEach((i) => {
        const customer = this.customerForId(i.idCustomer)

        i.customerName = customer ? `${customer.name} ${customer.surname}` : ""
        i.customerCompany = customer ? customer.company : ""
        i.customerEmail = customer ? customer.email : ""
        i.customerCountry = customer ? customer.country : ""

        i.maxCals = i.licenceFile.type === 1 ? i.licenceFile.maxInstances : i.licenceFile.maxTotalSessions
        i.maxCalsSufix = i.licenceFile.type === 1 ? "device" : "server"
        // Booleans, because both tables colour these with `x` and `x === false` and anything
        // else lands in neither class. A perpetual licence counts as in date here as well as
        // in the per-customer list, which used to disagree with this one about them.
        i.validUntilActual = Boolean(
          datesService.isAfter(i.licenceFile.validUntil) || i.licenceFile.perpetual
        )
        i.maintenanceUntilActual = Boolean(datesService.isAfter(i.licenceFile.maintenanceUntil))

        const foundModule = modules.find(
          (m) => m.value === i.licenceFile.module || m.value === i.licenceFile.targetModule
        )
        i.moduleDisplay = foundModule ? foundModule.text : "-"
      })
      this.allLicences = licences
    },
    async saveCustomer(customer) {
      try {
        this.savingCustomer = true
        const addResult = await customersService.save(customer)
        await this.fetchAllCustomers()
        return addResult
      } finally {
        this.savingCustomer = false
      }
    },
    async deleteCustomer(customer) {
      await customersService.deleteCustomer(customer)
      await this.fetchAllCustomers()
    }
  }
})
